import { registerCustomSymbols, type CustomSymbol } from './custom';
import { SHEET } from './geometry';
import { t as $t } from './i18n';
import { freeEnds, importDevices, symbolColoursOf, type PlacedText } from './pdfDevices';
import type { PdfPageData, PdfText } from './pdfExtract';
import { applyParts, partRows } from './pdfParts';
import { newId, ptypeFromName } from './tagging';
import type { Page, PType, Pt, Sym, Wire } from './types';

/*
 * Schematic PDFs exported from EPLAN (vector, A3) → EnerCAD pages. The wire
 * colour is found from the drawing (the stroke colour with the most straight
 * horizontal / vertical length); wire segments become wires, filled arrows at
 * wire ends become interruption points named after the text beyond them, and
 * bold texts sitting on a wire become its wire number. Pages without wiring
 * (cover, contents, reports) are left out; EnerCAD makes its own.
 */

export interface PdfImportResult {
  pages: Page[];
  /** user symbols the devices are drawn with (the project stores them) */
  symbols: CustomSymbol[];
  warnings: string[];
  stats: { pages: number; skipped: number; wires: number; breaks: number; wireNumbers: number; devices: number; contacts: number; terminals: number; untagged: number; texts: number; parts: number };
}

export interface PdfImportOptions {
  /** wire colour "#rrggbb"; found from the drawing when left out */
  wireColour?: string;
}

/** Coordinates kept to half a millimetre: EPLAN draws on a 1 mm raster, not on 4 mm. */
const snap = (v: number) => Math.round(v * 2) / 2;
const EPS = 0.2;
const isGrey = (c: string) => /^#([0-9a-f]{2})\1\1$/i.test(c);
/** Cross-reference texts such as "13.3", "/ 40.0", "/17.1" and "10.6 /". */
const XREF = /^(\/\s*)?\d+\.\d+$|^\/\s*\d+(\.\d+)?$|^\d+\.\d+\s*\/$|^\/$/;

type Seg = { a: Pt; b: Pt };
interface Arrow { tip: Pt; base: Pt; box: [number, number, number, number] }
const horiz = (s: Seg) => Math.abs(s.a[1] - s.b[1]) < EPS;
const vert = (s: Seg) => Math.abs(s.a[0] - s.b[0]) < EPS;
const len = (s: Seg) => Math.hypot(s.b[0] - s.a[0], s.b[1] - s.a[1]);

/** Straight horizontal / vertical segments of the paths drawn in `colour` (page mm). */
function segmentsOf(page: PdfPageData, colour: string): Seg[] {
  const out: Seg[] = [];
  for (const p of page.paths) {
    if (p.stroke !== colour || p.fill === colour) continue;
    for (const poly of p.polys)
      for (let i = 0; i + 1 < poly.pts.length; i++) {
        const s = { a: poly.pts[i], b: poly.pts[i + 1] };
        if ((horiz(s) || vert(s)) && len(s) >= 0.6) out.push(s);
      }
  }
  return out;
}

/**
 * The wire colour: the stroke colour with the most length in long (10 mm or more) horizontal / vertical strokes of
 * open lines. Boxes and frames are closed shapes and symbol strokes are short, so they do not count. Black and
 * greys are left out (frames, texts).
 */
export function findWireColour(pages: PdfPageData[]): string | undefined {
  const total = new Map<string, number>();
  for (const pg of pages)
    for (const p of pg.paths) {
      if (!p.stroke || isGrey(p.stroke) || p.fill) continue;
      for (const poly of p.polys) {
        if (poly.closed) continue;
        for (let i = 0; i + 1 < poly.pts.length; i++) {
          const s = { a: poly.pts[i], b: poly.pts[i + 1] };
          if ((horiz(s) || vert(s)) && len(s) >= 10) total.set(p.stroke, (total.get(p.stroke) ?? 0) + len(s));
        }
      }
    }
  return [...total.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
}

/** Length in long (10 mm or more) horizontal / vertical strokes of open lines, per stroke colour. */
function wiringLength(pages: PdfPageData[]): Map<string, number> {
  const total = new Map<string, number>();
  for (const pg of pages)
    for (const p of pg.paths) {
      if (!p.stroke || p.fill) continue;
      for (const poly of p.polys) {
        if (poly.closed) continue;
        for (let i = 0; i + 1 < poly.pts.length; i++) {
          const s = { a: poly.pts[i], b: poly.pts[i + 1] };
          if ((horiz(s) || vert(s)) && len(s) >= 10) total.set(p.stroke, (total.get(p.stroke) ?? 0) + len(s));
        }
      }
    }
  return total;
}

/**
 * Drawings in one colour (old AutoCAD schematics: wires and symbols both black) cannot be told apart by colour.
 * There the horizontal / vertical strokes are split where one ends on another; strokes that lie on a closed loop
 * (boxes, coil rectangles, terminal tables, the frame) are drawing, the rest (wiring branches like a tree; contacts
 * and coils interrupt it) are wires.
 */
function splitLoops(runs: Seg[]): { wires: Seg[]; outlines: Seg[] } {
  const key = (p: Pt) => `${p[0]},${p[1]}`;
  // cut every run where another run ends on it (a T), never at plain crossings
  const cuts = runs.map((s) => {
    const h = horiz(s), lo = h ? Math.min(s.a[0], s.b[0]) : Math.min(s.a[1], s.b[1]), hi = h ? Math.max(s.a[0], s.b[0]) : Math.max(s.a[1], s.b[1]);
    const at = new Set<number>([lo, hi]);
    for (const o of runs)
      for (const p of [o.a, o.b]) {
        const c = h ? p[1] : p[0], v = h ? p[0] : p[1];
        if (Math.abs(c - (h ? s.a[1] : s.a[0])) < 0.01 && v > lo + 0.01 && v < hi - 0.01) at.add(v);
      }
    const vs = [...at].sort((a, b) => a - b);
    return vs.slice(1).map((v, i): Seg => (h ? { a: [vs[i], s.a[1]], b: [v, s.a[1]] } : { a: [s.a[0], vs[i]], b: [s.a[0], v] }));
  });
  const edges = cuts.flat();
  const ids = new Map<string, number>();
  const node = (p: Pt) => ids.get(key(p)) ?? (ids.set(key(p), ids.size), ids.size - 1);
  const adj: [number, number][][] = [];
  edges.forEach((e, i) => {
    const u = node(e.a), v = node(e.b);
    (adj[u] ??= []).push([v, i]);
    (adj[v] ??= []).push([u, i]);
  });
  // bridges (Tarjan, iterative): an edge on no loop
  const n = ids.size, disc = new Array<number>(n).fill(-1), low = new Array<number>(n).fill(0), bridge = new Set<number>();
  let time = 0;
  for (let s = 0; s < n; s++) {
    if (disc[s] >= 0) continue;
    const stack: [number, number, number][] = [[s, -1, 0]];
    disc[s] = low[s] = time++;
    while (stack.length) {
      const top = stack[stack.length - 1], [u, via] = top, list = adj[u] ?? [];
      if (top[2] < list.length) {
        const [v, e] = list[top[2]++];
        if (e === via) continue;
        if (disc[v] < 0) {
          disc[v] = low[v] = time++;
          stack.push([v, e, 0]);
        } else low[u] = Math.min(low[u], disc[v]);
      } else {
        stack.pop();
        if (stack.length) {
          const p = stack[stack.length - 1][0];
          low[p] = Math.min(low[p], low[u]);
          if (low[u] > disc[p]) bridge.add(via);
        }
      }
    }
  }
  return { wires: edges.filter((_, i) => bridge.has(i)), outlines: edges.filter((_, i) => !bridge.has(i)) };
}

/**
 * Strokes that touch no wiring and are only a few millimetres long all together (the legs of an arrow, the tick of a
 * contact): drawing in a one-colour sheet, not wire.
 */
function dropSpecks(runs: Seg[]): Seg[] {
  const parent = runs.map((_, i) => i);
  const find = (i: number): number => {
    // iterative with path compression: a long chain in a big drawing must not overflow the stack
    let r = i;
    while (parent[r] !== r) r = parent[r];
    while (parent[i] !== r) [parent[i], i] = [r, parent[i]];
    return r;
  };
  for (let i = 0; i < runs.length; i++)
    for (let j = i + 1; j < runs.length; j++) {
      const a = runs[i], b = runs[j];
      if (segOn(a.a, b.a, b.b) || segOn(a.b, b.a, b.b) || segOn(b.a, a.a, a.b) || segOn(b.b, a.a, a.b)) parent[find(i)] = find(j);
    }
  const total = new Map<number, number>();
  runs.forEach((r, i) => total.set(find(i), (total.get(find(i)) ?? 0) + len(r)));
  return runs.filter((_, i) => total.get(find(i))! >= 5);
}

/** Merges touching or overlapping collinear segments into the longest straight runs (snapped). */
function mergeRuns(segs: Seg[]): Seg[] {
  const out: Seg[] = [];
  const groups = new Map<string, [number, number][]>();
  for (const s of segs) {
    const h = horiz(s);
    const key = (h ? 'h' : 'v') + snap(h ? s.a[1] : s.a[0]);
    const lo = h ? Math.min(s.a[0], s.b[0]) : Math.min(s.a[1], s.b[1]), hi = h ? Math.max(s.a[0], s.b[0]) : Math.max(s.a[1], s.b[1]);
    (groups.get(key) ?? groups.set(key, []).get(key)!).push([snap(lo), snap(hi)]);
  }
  for (const [key, spans] of groups) {
    const c = Number(key.slice(1)), h = key[0] === 'h';
    spans.sort((a, b) => a[0] - b[0]);
    let [lo, hi] = spans[0];
    const emit = () => {
      if (hi - lo >= 0.5) out.push(h ? { a: [lo, c], b: [hi, c] } : { a: [c, lo], b: [c, hi] });
    };
    for (const [a, b] of spans.slice(1)) {
      if (a <= hi + 0.01) hi = Math.max(hi, b);
      else {
        emit();
        [lo, hi] = [a, b];
      }
    }
    emit();
  }
  return out;
}

/** Joins runs meeting end to end at a corner (and nothing else there) into one polyline wire. */
function chainWires(runs: Seg[]): Pt[][] {
  const key = (p: Pt) => `${p[0]},${p[1]}`;
  const onInterior = (p: Pt, s: Seg) =>
    horiz(s) ? Math.abs(p[1] - s.a[1]) < 0.01 && p[0] > Math.min(s.a[0], s.b[0]) + 0.01 && p[0] < Math.max(s.a[0], s.b[0]) - 0.01
      : Math.abs(p[0] - s.a[0]) < 0.01 && p[1] > Math.min(s.a[1], s.b[1]) + 0.01 && p[1] < Math.max(s.a[1], s.b[1]) - 0.01;
  const ends = new Map<string, number[]>();
  runs.forEach((s, i) => [s.a, s.b].forEach((p) => (ends.get(key(p)) ?? ends.set(key(p), []).get(key(p))!).push(i)));
  // a corner can be merged when exactly two runs end there and no other run passes through it
  const mergeable = (p: Pt) => ends.get(key(p))!.length === 2 && !runs.some((s) => onInterior(p, s));
  const used = new Set<number>(), out: Pt[][] = [];
  for (let i = 0; i < runs.length; i++) {
    if (used.has(i)) continue;
    used.add(i);
    let pts: Pt[] = [runs[i].a, runs[i].b];
    for (const dir of [1, -1]) {
      for (;;) {
        const end = dir === 1 ? pts[pts.length - 1] : pts[0];
        if (!mergeable(end)) break;
        const j = ends.get(key(end))!.find((n) => !used.has(n));
        if (j == null) break;
        used.add(j);
        const next = key(runs[j].a) === key(end) ? runs[j].b : runs[j].a;
        pts = dir === 1 ? [...pts, next] : [next, ...pts];
      }
    }
    out.push(pts);
  }
  return out;
}

/** Short slanted strokes in the wire colour (page mm): EPLAN's T-junction marks and wire number ticks. */
function slantsOf(page: PdfPageData, colour: string): Seg[] {
  const out: Seg[] = [];
  for (const p of page.paths) {
    if (p.stroke !== colour || p.fill === colour) continue;
    for (const poly of p.polys)
      for (let i = 0; i + 1 < poly.pts.length; i++) {
        const s = { a: poly.pts[i], b: poly.pts[i + 1] };
        if (!horiz(s) && !vert(s) && len(s) >= 1 && len(s) <= 4.5) out.push(s);
      }
  }
  return out;
}

/**
 * EPLAN draws a T-junction as a branch ending a little short of the wire it
 * joins, with a slanted mark in between. Such a branch end is extended along
 * its own direction onto the wire, so the two are connected.
 */
function closeJunctions(wires: Wire[], slants: Seg[]) {
  const segsOf = (w: Wire) => w.pts.slice(1).map((q, i) => ({ a: w.pts[i], b: q }));
  for (const w of wires)
    for (const at of [0, w.pts.length - 1]) {
      const e = w.pts[at], nb = w.pts[at === 0 ? 1 : at - 1];
      if (!slants.some((s) => Math.hypot(s.a[0] - e[0], s.a[1] - e[1]) < 0.8 || Math.hypot(s.b[0] - e[0], s.b[1] - e[1]) < 0.8)) continue;
      const l = Math.hypot(e[0] - nb[0], e[1] - nb[1]) || 1, u: Pt = [(e[0] - nb[0]) / l, (e[1] - nb[1]) / l];
      let best: Pt | null = null, bd = 5;
      for (const o of wires) {
        if (o === w) continue;
        for (const s of segsOf(o)) {
          // a segment across the branch direction, within 5 mm ahead of the end
          if (Math.abs(u[0]) > 0.5 && vert(s)) {
            const d = (s.a[0] - e[0]) * Math.sign(u[0]);
            if (d > 0 && d < bd && e[1] >= Math.min(s.a[1], s.b[1]) - 0.01 && e[1] <= Math.max(s.a[1], s.b[1]) + 0.01) {
              bd = d;
              best = [s.a[0], e[1]];
            }
          } else if (Math.abs(u[1]) > 0.5 && horiz(s)) {
            const d = (s.a[1] - e[1]) * Math.sign(u[1]);
            if (d > 0 && d < bd && e[0] >= Math.min(s.a[0], s.b[0]) - 0.01 && e[0] <= Math.max(s.a[0], s.b[0]) + 0.01) {
              bd = d;
              best = [e[0], s.a[1]];
            }
          }
        }
      }
      if (best) w.pts[at] = best;
    }
}

/** Filled triangles in the wire colour: the arrows of interruption points. */
function arrowsOf(page: PdfPageData, colour: string): Arrow[] {
  const out: Arrow[] = [];
  for (const p of page.paths) {
    if (p.fill !== colour) continue;
    for (const poly of p.polys) {
      const v = poly.pts.filter((q, i, a) => i === 0 || Math.hypot(q[0] - a[i - 1][0], q[1] - a[i - 1][1]) > 0.05);
      if (v.length > 1 && Math.hypot(v[0][0] - v[v.length - 1][0], v[0][1] - v[v.length - 1][1]) < 0.05) v.pop();
      if (v.length !== 3) continue;
      const d = (i: number, j: number) => Math.hypot(v[i][0] - v[j][0], v[i][1] - v[j][1]);
      // the base is the shortest side; the tip is the corner opposite it
      const [i, j, t] = ([[0, 1, 2], [1, 2, 0], [0, 2, 1]] as const).slice().sort((e, f) => d(e[0], e[1]) - d(f[0], f[1]))[0];
      if (d(i, j) > 5) continue;
      out.push({
        tip: v[t], base: [(v[i][0] + v[j][0]) / 2, (v[i][1] + v[j][1]) / 2],
        box: [Math.min(...v.map((q) => q[0])), Math.min(...v.map((q) => q[1])), Math.max(...v.map((q) => q[0])), Math.max(...v.map((q) => q[1]))],
      });
    }
  }
  return out;
}

/** Is p on the segment a–b (within `tol`, 0.3 mm)? */
function segOn(p: Pt, a: Pt, b: Pt, tol = 0.3): boolean {
  const dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy) < tol;
}

/** Rotation of the brk symbol so its arrow points along `d` (down, left, up, right). */
const rotFor = (d: Pt) => (Math.abs(d[1]) >= Math.abs(d[0]) ? (d[1] > 0 ? 0 : 2) : d[0] < 0 ? 1 : 3);

/** Middle of a text (page mm): halfway along the baseline, half a cap height above it. */
const centre = (t: PdfText): Pt => {
  const a = (t.angle * Math.PI) / 180, h = t.size * 0.35;
  return [t.x + (Math.cos(a) * t.w) / 2 + Math.sin(a) * h, t.y + (Math.sin(a) * t.w) / 2 - Math.cos(a) * h];
};

/**
 * Potential type from an interruption point name, only where the name is unambiguous: phases (L1, 1L2, R, S, T),
 * N, PE, L+ / +24V and L- / 0V / M. Project-specific names such as "+30" or "-2" get none (no colour rather than a wrong one).
 */
function ptypeOf(n: string): PType {
  if (/^\d*L\d$|^[RST]$/i.test(n)) return 'L';
  if (n === 'N' || n === 'PE') return ptypeFromName(n);
  if (/^(L\+|\+24V?|24V\+|\+24 ?VDC)$/i.test(n)) return 'DC+';
  if (/^(L-|M|0V|-24V?|24V-|0 ?VDC)$/i.test(n)) return 'DC-';
  return '';
}

/** Converts the pages of an EPLAN schematic PDF. */
export function pdfToPages(doc: PdfPageData[], opts: PdfImportOptions = {}): PdfImportResult {
  const warnings: string[] = [];
  const stats = { pages: 0, skipped: 0, wires: 0, breaks: 0, wireNumbers: 0, devices: 0, contacts: 0, terminals: 0, untagged: 0, texts: 0, parts: 0 };
  // one-colour drawings (black wires and symbols): wires are told from symbols by their shape, not their colour
  const lengths = wiringLength(doc), coloured = findWireColour(doc);
  const blackLen = Math.max(0, ...[...lengths.entries()].filter(([c]) => isGrey(c)).map(([, l]) => l));
  const mono = !opts.wireColour && blackLen > 0 && (!coloured || (lengths.get(coloured) ?? 0) < blackLen * 0.03);
  const colour = opts.wireColour ?? (mono ? [...lengths.entries()].filter(([c]) => isGrey(c)).sort((a, b) => b[1] - a[1])[0][0] : coloured);
  if (!colour) return { pages: [], symbols: [], warnings: [$t("PDF dosyasında tel çizgisi bulunamadı; dosya vektörel bir şema PDF'i olmalı.")], stats };
  // in a one-colour drawing the few coloured lines (a blue neutral, a red supply) are drawn like the rest (white masks stay)
  const ink = (c: string | undefined) => (c && c.toLowerCase() !== '#ffffff' ? colour : c);
  if (mono) doc = doc.map((pg) => ({ ...pg, paths: pg.paths.map((p) => ({ ...p, ...(p.stroke ? { stroke: ink(p.stroke) } : {}), ...(p.fill ? { fill: ink(p.fill) } : {}) })) }));

  // pass 1: wiring per page in page millimetres; pages without wiring (cover, contents, reports) are left out
  const near = (p: Pt, a: Arrow, m: number) => p[0] >= a.box[0] - m && p[0] <= a.box[2] + m && p[1] >= a.box[1] - m && p[1] <= a.box[3] + m;
  const found = doc.flatMap((pg, pi) => {
    const arrows = arrowsOf(pg, colour);
    // the arrow's own outline and the short stub beside it belong to the interruption point, not to the wiring
    let runs = mergeRuns(segmentsOf(pg, colour).filter((s) => !arrows.some((a) => near(s.a, a, 0.05) && near(s.b, a, 0.05))))
      .filter((s) => !(len(s) < 3 && arrows.some((a) => near(s.a, a, 2) && near(s.b, a, 2))));
    let outlines: Seg[] = [];
    if (mono) {
      ({ wires: runs, outlines } = splitLoops(runs));
      runs = dropSpecks(runs);
    }
    // one-colour pages: lines across the whole page that never meet are the rules of a report table, not wiring
    const across = (r: Seg) => len(r) > 0.7 * (horiz(r) ? pg.w : pg.h);
    const rules = mono && runs.every(across) && !runs.some((r, i) => runs.some((o, j) => i !== j && (segOn(r.a, o.a, o.b) || segOn(r.b, o.a, o.b))));
    if (rules || runs.reduce((n, s) => n + len(s), 0) < 60) {
      stats.skipped++;
      return [];
    }
    return [{ pg, pi, runs, arrows, outlines }];
  });

  // one placement for the whole document: the wiring of every page fitted into the drawing area
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const f of found)
    for (const s of f.runs)
      for (const p of [s.a, s.b]) {
        x0 = Math.min(x0, p[0]);
        y0 = Math.min(y0, p[1]);
        x1 = Math.max(x1, p[0]);
        y1 = Math.max(y1, p[1]);
      }
  const area = { x0: SHEET.X0 + 12, x1: SHEET.X1 - 12, y0: SHEET.Y0 + SHEET.HDR + 4, y1: SHEET.TB_Y - 4 };
  // A3 sheets (EPLAN) come out at about 1:1, never enlarged; smaller sheets (A4, old drawings) up to 1.5 times
  const cap = Math.max(0, ...found.map((f) => f.pg.w)) >= 400 ? 1 : 1.5;
  const k = found.length ? Math.min(cap, (area.x1 - area.x0) / (x1 - x0 || 1), (area.y1 - area.y0) / (y1 - y0 || 1)) : 1;
  const ox = area.x0 + (area.x1 - area.x0 - (x1 - x0) * k) / 2 - x0 * k, oy = area.y0 + (area.y1 - area.y0 - (y1 - y0) * k) / 2 - y0 * k;
  const Tr = (p: Pt): Pt => [p[0] * k + ox, p[1] * k + oy];
  const T = (p: Pt): Pt => [snap(p[0] * k + ox), snap(p[1] * k + oy)];
  const drawn: [number, number, number, number] = [x0 * k + ox - 5, y0 * k + oy - 5, x1 * k + ox + 5, y1 * k + oy + 5];

  const pages: Page[] = [];
  const done: { pg: PdfPageData; pi: number; page: Page; texts: PlacedText[]; used: Set<PdfText>; outlines: Seg[] }[] = [];
  for (const { pg, pi, runs, arrows, outlines } of found) {
    const page: Page = { id: newId(), desc: (pg.title.replace(/^\/?\d+\s+/, '') || $t('Sayfa {0}', pi + 1)).slice(0, 120), syms: [], wires: [] };
    const wires: Wire[] = chainWires(mergeRuns(runs.map((s) => ({ a: T(s.a), b: T(s.b) })))).map((pts) => ({ id: newId(), pts, cs: '', col: '' }));
    closeJunctions(wires, slantsOf(pg, colour).map((s) => ({ a: Tr(s.a), b: Tr(s.b) })));
    // texts in the wire colour are wire marks, except in one-colour drawings where every text is black
    const texts = pg.texts.filter((t) => mono || t.colour !== colour).map((t) => ({ t, c: T(centre(t)) }));
    const used = new Set<PdfText>();

    // interruption points: an arrow at a wire end, named by the text beyond that end
    const ends = wires.flatMap((w) => [
      { p: w.pts[0], from: w.pts[1] },
      { p: w.pts[w.pts.length - 1], from: w.pts[w.pts.length - 2] },
    ]);
    for (const ar of arrows) {
      const tip = T(ar.tip), base = T(ar.base);
      let best: { p: Pt; from: Pt; d: number } | null = null;
      for (const e of ends) {
        const d = Math.min(Math.hypot(e.p[0] - tip[0], e.p[1] - tip[1]), Math.hypot(e.p[0] - base[0], e.p[1] - base[1]));
        if (d < 3.5 && (!best || d < best.d)) best = { ...e, d };
      }
      if (!best) continue;
      // outwards: along the wire, away from its body
      const ol = Math.hypot(best.p[0] - best.from[0], best.p[1] - best.from[1]) || 1;
      const u: Pt = [(best.p[0] - best.from[0]) / ol, (best.p[1] - best.from[1]) / ol];
      let name: PdfText | null = null, nd = Infinity;
      for (const { t, c } of texts) {
        if (used.has(t) || XREF.test(t.s) || t.bold) continue;
        const along = (c[0] - best.p[0]) * u[0] + (c[1] - best.p[1]) * u[1], across = Math.abs((c[0] - best.p[0]) * u[1] - (c[1] - best.p[1]) * u[0]);
        if (along > 0 && along < 25 && across < 3.5 && along + across * 3 < nd) {
          nd = along + across * 3;
          name = t;
        }
      }
      // an arrow without a name only points at something drawn (a motor, a field device)
      if (!name) continue;
      used.add(name);
      const props: Sym['props'] = { name: name.s.slice(0, 40) };
      const pt = ptypeOf(name.s);
      if (pt) props.ptype = pt;
      page.syms.push({ id: newId(), type: 'brk', x: best.p[0], y: best.p[1], rot: rotFor(u), tag: '', desc: '', part: '', maker: '', props });
      stats.breaks++;
    }

    // wire numbers: bold texts lying along a wire, at most 3 mm from it
    // (one-colour drawings write them in plain type: plain numbers beside the middle of a wire, either way round)
    for (const { t, c } of texts) {
      const plain = mono && !t.bold && /^\d{1,5}[A-Z]?$/.test(t.s);
      if (used.has(t) || !(t.bold || plain) || XREF.test(t.s) || t.s.length > 16) continue;
      const vertText = Math.abs(Math.abs(t.angle) - 90) < 5;
      let best: { w: Wire; d: number } | null = null;
      for (const w of wires)
        for (let i = 0; i + 1 < w.pts.length; i++) {
          const s = { a: w.pts[i], b: w.pts[i + 1] };
          const lo = horiz(s) ? Math.min(s.a[0], s.b[0]) : Math.min(s.a[1], s.b[1]), hi = horiz(s) ? Math.max(s.a[0], s.b[0]) : Math.max(s.a[1], s.b[1]);
          const along = horiz(s) ? c[0] : c[1], d = horiz(s) ? Math.abs(c[1] - s.a[1]) : Math.abs(c[0] - s.a[0]);
          if (horiz(s) !== vertText) {
            const m = Math.min(2.5, (hi - lo) / 4);
            if (plain ? along >= lo + m && along <= hi - m && d < 3.4 : along >= lo - 1 && along <= hi + 1 && d < 3) if (!best || d < best.d) best = { w, d };
          } else if (plain) {
            // across the wire: the text stands beside it, its near edge within 2.5 mm
            const gap = d - (t.w * k) / 2;
            const m = Math.min(2.5, (hi - lo) / 4);
            if (along >= lo + m && along <= hi - m && gap < 2.5 && (!best || gap < best.d)) best = { w, d: gap };
          }
        }
      if (!best) continue;
      used.add(t);
      if (!best.w.no) {
        best.w.no = t.s;
        stats.wireNumbers++;
      }
    }

    page.wires = wires;
    stats.wires += wires.length;
    stats.pages++;
    pages.push(page);
    done.push({ pg, pi, page, texts, used, outlines });
  }

  // devices: in the colour the free wire ends touch most, drawn with their EPLAN graphics
  const symbolColours = mono ? [colour] : symbolColoursOf(done.map(({ pg, page }) => ({ pg, T: Tr, ends: freeEnds(page.wires, page.syms) })), colour);
  const library = new Map<string, CustomSymbol>();
  for (const { pg, page, texts, used, outlines } of done) {
    // one-colour drawings: the strokes that became wires are not part of any symbol
    const onWire = mono
      ? (a: Pt, b: Pt) => {
          const s = { a, b };
          if (!horiz(s) && !vert(s)) return false;
          const m: Pt = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
          // along a wire, not across its end (the side of a box a wire ends on stays drawing)
          // (wires are snapped to the grid, the drawing is not: up to 0.6 mm apart)
          return page.wires.some((w) => w.pts.some((q, i) => i > 0 && horiz({ a: w.pts[i - 1], b: q }) === horiz(s) && segOn(m, w.pts[i - 1], q, 0.6)));
        }
      : undefined;
    const r = importDevices({ pg, T: Tr, k, wireColour: colour, symbolColours, wires: page.wires, syms: page.syms, texts, used, library, area: drawn, skip: onWire, mono });
    page.syms.push(...r.syms);
    for (const key of ['devices', 'contacts', 'terminals', 'untagged', 'texts'] as const) stats[key] += r.stats[key];
  }
  // where a device sits: the PDF page name and the frame column (0…9 across the top of a schematic page)
  const heads = (done[0]?.pg.texts ?? []).filter((t) => /^\d$/.test(t.s) && t.y < 10).map((t) => ({ n: Number(t.s), x: t.x + t.w / 2 })).sort((a, b) => a.x - b.x);
  const column = (s: Sym) => (heads.length < 5 ? null : heads.reduce((a, b) => (Math.abs(b.x - (s.x - ox) / k) < Math.abs(a.x - (s.x - ox) / k) ? b : a)).n);
  const named = done.map(({ pg, page }) => ({ page, name: pg.title.replace(/^\//, '').split(/\s+/)[0] ?? '', column }));
  const pageName = new Map(named.flatMap(({ page, name }) => page.syms.map((s) => [s.id, name] as const)));
  const rows = partRows(doc);

  // a device drawn only as contacts (push buttons, selector switches) has one of them as main function: the one at
  // the place the parts list gives, else the first
  const defs = new Map([...library.values()].map((c) => [c.key, c]));
  const tagged = new Map<string, Sym[]>();
  for (const { page } of done) for (const s of page.syms) if (s.tag && defs.has(s.type)) (tagged.get(s.tag) ?? tagged.set(s.tag, []).get(s.tag)!).push(s);
  for (const [tag, group] of tagged) {
    if (group.some((s) => defs.get(s.type)!.role !== 'aux')) continue;
    const listed = rows.find((r) => r.tag.replace(/^[-=+]+/, '').toUpperCase() === tag.toUpperCase());
    const s = group.find((x) => listed && pageName.get(x.id) === listed.page && column(x) === listed.col) ?? group[0];
    const aux = defs.get(s.type)!, key = `${aux.key.slice(0, 31)}_m`;
    if (!defs.has(key)) {
      const { kind: _, ...rest } = aux;
      const main: CustomSymbol = { ...rest, key, role: 'main' };
      defs.set(key, main);
      library.set(`main:${aux.key}`, main);
    }
    s.type = key;
    stats.contacts--;
  }
  const used = new Set(done.flatMap(({ page }) => page.syms.map((s) => s.type)));
  for (const [sig, c] of library) if (!used.has(c.key)) library.delete(sig);
  const symbols = registerCustomSymbols([...library.values()]);

  // part numbers from the parts list report, matched by page, column and tag
  if (rows.length) {
    stats.parts = applyParts(rows, named, new Map(symbols.map((c) => [c.key, c])));
    if (stats.parts < rows.length) warnings.push($t('Ürün listesindeki {0} satırdan {1} tanesi bir cihazla eşleşti.', rows.length, stats.parts));
  }
  // devices cut the wires that ran through them
  stats.wires = pages.reduce((n, p) => n + p.wires.length, 0);
  if (stats.untagged) warnings.push($t('{0} cihazın etiketi okunamadı; bunları editörde etiketleyin.', stats.untagged));
  if (stats.skipped) warnings.unshift($t('{0} sayfada bağlantı çizimi yok (kapak, içindekiler, raporlar); bunlar alınmadı.', stats.skipped));
  return { pages, symbols, warnings, stats };
}
